import React, { useMemo, useState } from 'react';
import AvFilters from '@components/AvFilters';
import { AvDateRangePicker, DatePickerStartAdornment, DateValue, DwQueryRequest, Filter } from '@fabric-ui/components';
import { History as HistoryData } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useMeasurementsList } from '@views/Reports/hooks';
import { useAvContext } from '../../../context/AvContextProvider';
import { getLast3MonthsDate } from '../utils';
import AgentStatus from './agentStatus';
import AgentTiles from './AgentTiles';
import MinResolution from './minResolution';
import { getTotalAlertsCountByAgents } from './queries';

const TITLE = 'AI Workforce Command Center';
const projName = 'uber_z_agent';
const DEFAULT_FILTER = { 'alert.type': [] } as Filter;

const Agents = () => {
  const [filters, setFilters] = useState<Filter>(DEFAULT_FILTER);
  const [dateRange, setDateRange] = useState<Date[]>([getLast3MonthsDate(), new Date()]);

  const onFilterChange = newFilters => {
    setFilters({ ...filters, ...newFilters });
  };
  const {
    accountEntities: { aggProjs },
  } = useAvContext();

  const theme = useTheme();

  const widgetBoxStyle = {
    flex: 1,
    borderRadius: '2px',
    zIndex: theme.zIndex.drawer,
    backgroundColor: 'var(--color-surface-base-primary)',
    overflow: 'auto',
    flexShrink: 0,
  };

  const { data: measurements = [] } = useMeasurementsList({ entityName: aggProjs[projName]?.entityTypeId?.name });

  const typeMap = useMemo(
    () => measurements.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), aggProjs[projName as string]?.typeMap),
    [measurements, aggProjs]
  );

  const dims = useMemo(
    () => [...(aggProjs.uber_alerts?.fieldList?.INTERACTIVE || []), ...(aggProjs.uber_z_agent?.fieldList?.INTERACTIVE || [])],
    [aggProjs]
  );

  const filterContext = { filters, dateRange, typeMap };

  const totalAlertCount = useQueryObject({
    queryObject: [getTotalAlertsCountByAgents(filterContext) as DwQueryRequest],
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data?.[0]?.cnt,
  });

  const statuses = {
    Alerts_Investigated: <AgentStatus totalAlertCount={totalAlertCount} filterContext={filterContext} />,
    Sub_Min_Resolution: <MinResolution totalAlertCount={totalAlertCount} filterContext={filterContext} />,
  };

  return (
    <Box sx={{ ...flex.col, width: '100%', gap: 2 }}>
      <Box sx={{ ...flex.justifyBetween }}>
        <Typography variant="h3" sx={{ mb: 1, color: 'var(--color-content-base-primary)' }}>
          {TITLE}
        </Typography>

        <AvDateRangePicker
          showDropdownIcon
          inputSx={{ width: '500px' }}
          value={{ from: dateRange[0] }}
          icon={<DatePickerStartAdornment tooltipText="Historical Data" icon={<HistoryData />} />}
          onChange={timeRange => {
            if (timeRange && timeRange.value) {
              setDateRange([(timeRange.value as DateValue).from, (timeRange.value as DateValue).to ?? new Date()]);
            } else {
              setDateRange([]);
            }
          }}
          showRelativeOptions
          isRange
          allowFutureDates={false}
          showOptions={false}
        />
      </Box>
      <Box sx={{ ...flex.justifyBetween }}>
        <AvFilters
          setFilters={setFilters}
          updateFilters={(key, value) => onFilterChange({ [key]: value })}
          filters={filters}
          onClearFilters={() => {
            setFilters(DEFAULT_FILTER);
          }}
          dims={dims}
          isFilterExpression
          isGlobalFilter
          isDraggable
        />
      </Box>
      <Box sx={{ ...flex.col, gap: 2, overflowY: 'auto' }}>
        <Box sx={{ ...flex.row, gap: 2, width: '100%', overflow: 'auto', minHeight: 137 }}>
          {Object.keys(statuses).map(s => (
            <Box key={s} sx={widgetBoxStyle}>
              {statuses[s]}
            </Box>
          ))}
        </Box>

        <AgentTiles filterContext={filterContext} />
      </Box>
    </Box>
  );
};

export default Agents;
